import { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	type TProductDataRegions,
	type TProductEdition,
} from '@atlassian/search-client/fetch-search-config';
import { type BackendExperiment, type ExperimentConfig } from '@atlassian/search-experiment/types';

import { QueryState } from '../../../common/constants/analytics';
import type { ProductKeys } from '../../../common/constants/products';
import type { IAnalyticsSearchPageResults } from '../../../common/types';
import { searchResultsLoadUFOExperienceSuccess } from '../../../common/ui/ufo/experiences';
import { onAdvancedSearchResultsShown } from '../../../common/utils/events/advanced-search-results-shown';
import { onFullPageSearchOperational } from '../../../common/utils/events/full-page-search-operational';
import type {
	CommonAttributes,
	NonPrivacySafeAttributes,
	ResultShown,
} from '../../../common/utils/events/types';
import { serveJiraNINFpsNLQNavigation } from '../../../common/utils/experiments/experiment-exports';
import { INTENT_DETECTION } from '../../../common/utils/intent-detection';
import { getIsBulkConnectBannerShown } from '../../../common/utils/quick-find/analytics';
import { getExtensionData } from '../../../common/utils/relay/get-extension-data';
import { useAnalytics } from '../../../controllers/analytics';
import { useSearchAndResultAnalyticsAttributes } from '../../../controllers/analytics/search-and-response-analytics';
import { useSocialSignalsAnalyticsAttributes } from '../../../controllers/analytics/social-signal-analytics';
import { useLogException } from '../../../controllers/error-boundary/utils';
import type { SelectedFiltersReturnType } from '../../../controllers/filters/selected/get-selected-filters';
import { getReferrerFromUrl, removeReferrerFromUrl } from '../../../controllers/remove-referrer';
import { usePrimaryProduct } from '../../../controllers/store/bootstrap';

type useSearchResultsAnalyticsArgs<TEdge> = {
	are3pResultsShown: boolean;
	backendExperimentRef: React.RefObject<BackendExperiment>;
	clearReferrer: () => void | Promise<void>;
	cloudId: string | undefined;
	commonAttributes: CommonAttributes;
	edges: TEdge[];
	experimentConfig: ExperimentConfig;
	getSelectedFilters: () => SelectedFiltersReturnType;
	intentDetectionTriggered?: boolean;
	isLoadingNext: boolean;
	isJiraIssueIndexed: boolean;
	isPaginatedQuery: boolean;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
	productDataRegions: TProductDataRegions | undefined;
	productEdition: TProductEdition | undefined;
	productsWithTimeoutsFromErrors: ProductKeys[] | undefined;
	referrer: string | undefined;
	resultsForAnalytics: IAnalyticsSearchPageResults;
	resultsShown: ResultShown[];
	searchQuery: string;
};

export const useSearchResultsAnalytics: <TEdge>({
	are3pResultsShown,
	backendExperimentRef,
	clearReferrer,
	cloudId,
	commonAttributes,
	edges,
	experimentConfig,
	getSelectedFilters,
	intentDetectionTriggered,
	isLoadingNext,
	isJiraIssueIndexed,
	isPaginatedQuery,
	nonPrivacySafeAttributes,
	productDataRegions,
	productEdition,
	productsWithTimeoutsFromErrors,
	referrer,
	resultsForAnalytics,
	resultsShown,
	searchQuery,
}: useSearchResultsAnalyticsArgs<TEdge>) => void = <TEdge>({
	are3pResultsShown,
	backendExperimentRef,
	clearReferrer,
	cloudId,
	commonAttributes,
	edges,
	experimentConfig,
	getSelectedFilters,
	intentDetectionTriggered,
	isLoadingNext,
	isJiraIssueIndexed,
	isPaginatedQuery,
	nonPrivacySafeAttributes,
	productDataRegions,
	productEdition,
	productsWithTimeoutsFromErrors,
	referrer,
	resultsForAnalytics,
	resultsShown,
	searchQuery,
}: useSearchResultsAnalyticsArgs<TEdge>) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const logException = useLogException();

	const intl = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const intentDetectionCohort = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false, // Exposure is fired when a user calls the intent detection API
		locale: intl.locale,
	})?.cohort;

	// Properties related to social signals
	const socialSignalsAttributes = useSocialSignalsAnalyticsAttributes(resultsForAnalytics);

	// Properties related to the search results in the result set
	const { searchAndResult } = useSearchAndResultAnalyticsAttributes(resultsForAnalytics);

	// We store these values in a ref as the references to the properties are not
	// stable we do not want to trigger the analytics events unnecessarily. This
	// allows to narrow down the dependency array of the useEffect hook that fires
	// the analytics events.
	const analyticsProperties = {
		are3pResultsShown,
		clearReferrer,
		cloudId,
		commonAttributes,
		experimentConfig,
		fireAnalyticsEvent,
		getSelectedFilters,
		intentDetectionTriggered,
		intentDetectionCohort,
		isJiraIssueIndexed,
		isPaginatedQuery,
		logException,
		nonPrivacySafeAttributes,
		productDataRegions,
		productEdition,
		productsWithTimeoutsFromErrors,
		referrer,
		resultsShown,
		searchAndResult,
		searchQuery,
		socialSignalsAttributes,
	};
	const analyticsPropertiesRef = useRef(analyticsProperties);
	analyticsPropertiesRef.current = analyticsProperties;

	useEffect(() => {
		const {
			are3pResultsShown,
			clearReferrer,
			commonAttributes: commonAttributesWithoutExtensionData,
			fireAnalyticsEvent,
			intentDetectionTriggered,
			intentDetectionCohort,
			isPaginatedQuery,
			logException,
			nonPrivacySafeAttributes,
			productsWithTimeoutsFromErrors,
			referrer: referrerFromStore,
			resultsShown,
			searchAndResult,
			searchQuery,
			socialSignalsAttributes,
		} = analyticsPropertiesRef.current;

		// Prefer the store value (kept in sync by the host app via onUpdate) but fall back to
		// reading directly from the URL at event-fire time. The fallback handles the case where
		// the host app does not update queryParams after a SPA navigation that adds ?referrer=
		// to the URL (e.g. zero-query suggestions navigating to the search page) and therefore
		// the store value is undefined even though the URL has a referrer.
		const referrerFromUrl = fg('qf_zero_query_referrer') ? getReferrerFromUrl() : undefined;
		const referrer = referrerFromStore ?? referrerFromUrl;

		if (isLoadingNext) {
			return;
		}

		const numResults = edges.length;
		const isBulkConnectBannerShown = getIsBulkConnectBannerShown();

		const commonAttributes = {
			...commonAttributesWithoutExtensionData,
			...getExtensionData(commonAttributesWithoutExtensionData.isCQL),
		};

		const requestedBackendExperiment: BackendExperiment = backendExperimentRef.current ?? undefined;

		fireAnalyticsEvent(
			onAdvancedSearchResultsShown({
				queryState: searchQuery ? QueryState.POSTQUERY : QueryState.PREQUERY,
				numResults,
				shownCount: numResults,
				are3pResultsShown,
				commonAttributes,
				nonPrivacySafeAttributes,
				socialSignalsAttributes,
				resultsShown,
				searchAndResult,
				requestedBackendExperiments: {
					backendExperiments: requestedBackendExperiment,
				},
				referrer: referrer && searchQuery ? referrer : undefined,
				isPaginatedQuery,
				isBulkConnectBannerShown,
				productsWithTimeout: productsWithTimeoutsFromErrors || [],
				...(intentDetectionTriggered !== undefined ? { intentDetectionTriggered } : {}),
				...(fg('rovo_qf_intent_detection_pattern_type_analytics')
					? {
							queryClassification: INTENT_DETECTION.value()?.mode,
							modelId: INTENT_DETECTION.value()?.modelId,
							cohort: intentDetectionCohort,
							patternType: INTENT_DETECTION.value()?.patternType,
							wordCount: commonAttributes.wordCount,
						}
					: {}),
			}),
		);

		fireAnalyticsEvent(
			onFullPageSearchOperational({
				success: true,
				commonAttributes,
			}),
		);

		searchResultsLoadUFOExperienceSuccess({
			queryLength: commonAttributes.queryLength,
			wordCount: commonAttributes.wordCount,
		});

		// Ensure that the referrer attribute is only used once
		clearReferrer();
		if (fg('qf_zero_query_referrer')) {
			removeReferrerFromUrl(logException);
		}
	}, [analyticsPropertiesRef, backendExperimentRef, edges, isLoadingNext]);
};
